import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Six digits, typed on a pad that fits any phone, or on a keyboard. Calls
 * back when the sixth lands.
 */
export function PinPad({
  title,
  hint,
  step,
  onPin,
  onBack,
  busy,
  below,
  inline = false,
}: {
  title: string;
  hint?: ReactNode;
  /** Where this is in a longer flow, such as "Step 1 of 3", shown small above the title. */
  step?: string | undefined;
  onPin: (pin: string) => void;
  /** A way back to the screen before, shown under the pad. */
  onBack?: () => void;
  busy?: boolean;
  /** Something small under the pad, such as a way to another wallet. */
  below?: ReactNode;
  /** In the page's own column rather than over everything; the browser's sign-in uses this. */
  inline?: boolean;
}) {
  const [pin, setPin] = useState('');
  // The latest state for the keyboard listener, which is attached once.
  const live = useRef({ pin, busy, onPin });
  live.current = { pin, busy, onPin };

  const press = (d: string): void => {
    const { pin: now, busy: wait, onPin: done } = live.current;
    if (wait === true || now.length >= 6) return;
    const next = now + d;
    if (next.length === 6) {
      setPin('');
      live.current.pin = '';
      done(next);
    } else {
      setPin(next);
      live.current.pin = next;
    }
  };
  const back = (): void => {
    const next = live.current.pin.slice(0, -1);
    setPin(next);
    live.current.pin = next;
  };

  // A computer has a keyboard: its digits and Backspace work as the pad does.
  useEffect(() => {
    // A box left focused underneath, such as an amount, would take the digits instead.
    const focused = document.activeElement;
    if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement)
      focused.blur();
    const onKey = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))
        return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        press(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
    // Attached once: press and back read everything through the ref.
  }, []);

  return (
    <div className={`screen center${inline ? ' pin-inline' : ' overlay'}`} data-testid="pinpad">
      {step !== undefined && (
        <p className="pin-step" data-testid="pin-step">
          {step}
        </p>
      )}
      <h2 className="title">{title}</h2>
      {hint !== undefined && <p className="hint">{hint}</p>}
      <div className="pin-dots" aria-label={`${pin.length} of 6 digits`}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`pin-dot${i < pin.length ? ' on' : ''}`} />
        ))}
      </div>
      <div className="pin-grid">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((k, i) => (
          <button
            key={i}
            type="button"
            className="pin-key"
            disabled={k === '' || busy === true}
            aria-label={k === '⌫' ? 'Delete' : undefined}
            data-testid={k === '⌫' ? 'pin-back' : `pin-${k}`}
            onClick={() => {
              if (k === '⌫') back();
              else press(k);
            }}
          >
            {k}
          </button>
        ))}
      </div>
      {below}
      {onBack !== undefined && (
        <button className="ghost" type="button" onClick={onBack} data-testid="pin-back-step">
          Back
        </button>
      )}
    </div>
  );
}
